import { Injectable } from '@angular/core';
import * as jose from 'jose';
import { ParticipanteAccessToken } from '../../core/models/participante-access-token';
import { PlanoAccessToken } from '../../core/models/plano-access-token';
import { TokenPortalPayload } from '../../core/models/token-portal-payload';

@Injectable({
  providedIn: 'root',
})
export class ExtractService {

  constructor() {}

  /** Decodifica o token do portal e valida formato, campos obrigatórios e expiração. */
  getDecodedAccessToken(token: string): TokenPortalPayload | null {
    if (!token) {
      return null;
    }

    try {
      const decoded = jose.decodeJwt<TokenPortalPayload>(token);
      const { organization, registration, cpfParticipante, nomePlano, exp } = decoded as TokenPortalPayload & {
        exp?: number;
      };

      if (!organization || !registration || !cpfParticipante || !nomePlano) {
        return null;
      }

      if (exp !== undefined && exp * 1000 <= Date.now()) {
        return null;
      }

      return { organization, registration, cpfParticipante, nomePlano };
    } catch {
      return null;
    }
  }

  /**
   * Lê o plano do accessToken salvo por token-verify, que vem no formato "Bearer <jwt>":
   * `value` vem do claim `plan` e `label` do `planDescription` (vazio se ausente). Devolve null
   * quando não há token, ele não pode ser lido ou não tem plano.
   */
  getPlanoAccessToken(accessToken: string | null): PlanoAccessToken | null {
    const jwt = accessToken?.replace(/^Bearer\s+/i, '').trim();
    if (!jwt) {
      return null;
    }

    try {
      const { plan, planDescription } = jose.decodeJwt<{ plan?: number | string; planDescription?: string }>(jwt);
      const value = Number(plan);
      if (plan == null || plan === '' || Number.isNaN(value)) {
        return null;
      }
      return { value, label: planDescription?.trim() ?? '' };
    } catch {
      return null;
    }
  }

  /**
   * Lê o CPF e a matrícula do token do portal usado no requerimento de benefício, aceitando os dois formatos de
   * token da aplicação (claim `cpf` ou `cpfParticipante`, e `registration`). Campo ausente vem null; token
   * ausente ou ilegível devolve null.
   */
  getIdentificacaoPortal(token: string | null): { cpf: string | null; matricula: string | null } | null {
    const jwt = token?.replace(/^Bearer\s+/i, '').trim();
    if (!jwt) {
      return null;
    }

    try {
      const { cpf, cpfParticipante, registration } = jose.decodeJwt<{
        cpf?: string | number;
        cpfParticipante?: string | number;
        registration?: string | number;
      }>(jwt);
      const texto = (valor: string | number | undefined) => String(valor ?? '').trim() || null;
      return { cpf: texto(cpf ?? cpfParticipante), matricula: texto(registration) };
    } catch {
      return null;
    }
  }

  /**
   * Lê do accessToken (formato "Bearer <jwt>") os dados que identificam o participante nas APIs:
   * cpf, plan, organization e registration. Devolve null quando não há token, ele não pode ser
   * lido ou falta algum desses claims.
   */
  getParticipanteAccessToken(accessToken: string | null): ParticipanteAccessToken | null {
    const jwt = accessToken?.replace(/^Bearer\s+/i, '').trim();
    if (!jwt) {
      return null;
    }

    try {
      const { cpf, plan, organization, registration } = jose.decodeJwt<{
        cpf?: string | number;
        plan?: string | number;
        organization?: string | number;
        registration?: string | number;
      }>(jwt);
      const texto = (valor: string | number | undefined) => String(valor ?? '').trim();
      const idPlanoUnificado = Number(plan);
      if (
        !texto(cpf) ||
        !texto(organization) ||
        !texto(registration) ||
        !texto(plan) ||
        Number.isNaN(idPlanoUnificado)
      ) {
        return null;
      }
      return {
        cpf: texto(cpf),
        idPlanoUnificado,
        idPatrocinadoraUnificado: texto(organization),
        matricula: texto(registration),
      };
    } catch {
      return null;
    }
  }
}
